import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import { useUserAccountsStore } from './user-accounts.store';

export const useAccountSelection = () => {
  const userAccountsStore = useUserAccountsStore();
  const { accounts, activeAccountId, selectedAccountIds } = storeToRefs(userAccountsStore);
  const { ensureValidSelectedAccountIds, setActiveAccountId, setSelectedAccountIds } =
    userAccountsStore;

  const selectedAccounts = computed(() => {
    const selectedIds = new Set(selectedAccountIds.value);

    return accounts.value.filter((account) => selectedIds.has(account.id));
  });

  const resolvedTransactionAccountIds = computed(() => {
    if (selectedAccountIds.value.length > 0) {
      return selectedAccountIds.value;
    }

    if (activeAccountId.value) {
      return [activeAccountId.value];
    }

    return [];
  });

  const resolvedTransactionAccountIdsKey = computed(() => {
    return [...resolvedTransactionAccountIds.value].sort().join(',');
  });

  const hasResolvedTransactionAccounts = computed(() => {
    return resolvedTransactionAccountIds.value.length > 0;
  });

  const isSelected = (accountId: string) => {
    return selectedAccountIds.value.includes(accountId);
  };

  const isActive = (accountId: string) => {
    return activeAccountId.value === accountId;
  };

  const ensureValidSelection = () => {
    ensureValidSelectedAccountIds();
  };

  const selectAccounts = (accountIds: string[]) => {
    setSelectedAccountIds(accountIds);
    ensureValidSelection();
  };

  const selectSingleAccount = (accountId: string) => {
    setSelectedAccountIds([accountId]);
    setActiveAccountId(accountId);
    ensureValidSelection();
  };

  const toggleAccountSelection = (accountId: string) => {
    const accountExists = accounts.value.some((account) => account.id === accountId);

    if (!accountExists) {
      return;
    }

    if (!isSelected(accountId)) {
      setSelectedAccountIds([...selectedAccountIds.value, accountId]);
      return;
    }

    if (selectedAccountIds.value.length === 1) {
      return;
    }

    setSelectedAccountIds(
      selectedAccountIds.value.filter((selectedId) => selectedId !== accountId),
    );
  };

  return {
    activeAccountId,
    selectedAccountIds,
    selectedAccounts,
    resolvedTransactionAccountIds,
    resolvedTransactionAccountIdsKey,
    hasResolvedTransactionAccounts,
    isSelected,
    isActive,
    toggleAccountSelection,
    selectAccounts,
    selectSingleAccount,
    ensureValidSelection,
  };
};
